/**
 * Which acts need a manager's PIN on the till, and whose PIN counts.
 *
 * Specification: ../test/approvals.spec.ts.
 *
 * The fraud statistics compare void and discount RATES per person, and a rate
 * that anybody can move on their own is a rate that measures nothing. So:
 *   - voiding a line the kitchen already has — food that may be cooked — and
 *     any discount or comp need somebody with the authority to say yes;
 *   - voiding a line nobody has sent yet (rung by mistake a second ago) does not,
 *     because it is the commonest correction on a till and costs nothing.
 * A manager acting on their own needs no second person: they are the approver.
 */

/** Roles that may approve, as `@gf/kernel/authz` names them. */
export const APPROVER_ROLES: ReadonlySet<string> = new Set(["owner", "admin", "manager", "shift_lead"]);

export type ApprovalAction = "VOID_SENT" | "VOID_UNSENT" | "ALLOWANCE" | "COMP" | "REFUND";

/**
 * Money going back out of the till, or given away whole, is narrower:
 * `payment.refund` and `order.comp` in `@gf/kernel/authz` are manager, admin
 * and owner — a shift lead may void a line or give a discount, but not take a
 * paid receipt back or comp a dish. The server checks the same catalogue
 * (`@gf/module-orders/approvals`) and flags an approval that breaks it.
 */
const MANAGER_APPROVERS: ReadonlySet<string> = new Set(["owner", "admin", "manager"]);

export const canApprove = (roleId: string | null | undefined, action?: ApprovalAction): boolean =>
  (action === "REFUND" || action === "COMP" ? MANAGER_APPROVERS : APPROVER_ROLES).has(roleId ?? "");

export function needsApproval(action: ApprovalAction, actingRoleId: string | null | undefined): boolean {
  if (action === "VOID_UNSENT") return false;
  return !canApprove(actingRoleId, action);
}
